import { extendTheme } from '@chakra-ui/react';
import type { StyleFunctionProps } from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';
import { FOLLOWING_COLOR, OFFLINE_COLOR, ONLINE_COLOR } from './component/styles';

const Black = '#000';

const theme = extendTheme({
    config: {
        initialColorMode: 'dark',
        useSystemColorMode: false,
    },

    styles: {
        global: (props: StyleFunctionProps) => ({
            body: {
                bg: mode('#DEDEDE', '#000')(props),
                '.discover-container, .following-container': {
                    '.discover-header, .following-header': {
                        border: mode('1px solid #FFF', 'none')(props),
                    },
                    '.live-six-parent': {
                        '.live-six': {
                            border: mode('1px solid #C3C3C3', 'none')(props),
                            bg: mode('#F2F2F2', '#2E2E2E')(props),
                        },
                    },
                },
            },
            '.divider': {
                borderColor: mode('#A4A4A4', '#d9d9d966')(props),
            },
            '.search-input': {
                bg: mode('#FFF', '#000')(props),
                color: mode('#000', '#FFF')(props),
            },
            '.browser-sidebar-component': {
                bg: mode('#C3C3C3', '#0D0D0D')(props),
                color: mode('#000', '#FFF')(props),
                a: {
                    '.browser-icon': {
                        color: mode('#000', '#FFF')(props),
                    },
                },
            },
            '.live-six-compact-mobile': {
                '.compact-streamer-info': {
                    border: mode('none', '1px solid #6EF1D5')(props),
                    '.compact-stack': {
                        border: mode('1px solid #C3C3C3', 'none')(props),
                        bg: mode('#F2F2F2', '#2E2E2E')(props),
                    },
                    '.compact-stack.following': {
                        border: mode(`1px solid ${FOLLOWING_COLOR}`, 'none')(props),
                    },
                },
                '.compact-streamer-info.offline': {
                    '.compact-stack': {
                        border: mode('1px solid ' + OFFLINE_COLOR, 'none')(props),
                    },
                },
            },
            '.live-six-browser': {
                bg: mode('#F2F2F2', '#2E2E2E')(props),
                border: mode('1px solid #C3C3C3', 'none')(props),
            },
            '.current-six-box': {
                bg: mode('#FFF', '#2E2E2E')(props),
                border: mode('1px solid #C3C3C3', 'none')(props),
            },
            '.current-six-box-mobile': {
                bg: mode('#FFF', '#2E2E2E')(props),
                border: mode('1px solid #C3C3C3', 'none')(props),
            },
            '.chakra-slide.chakra-modal__content.css-1o3pyl4': {
                bg: mode('#DEDEDE', '#2E2E2E')(props),
                borderRadius: '5px 5px 0px 0px',
                '.purchase-drawer-body': {
                    '.available-balance': {
                        borderTop: mode(
                            '1px solid #A4A4A4',
                            '1px solid rgba(255, 255, 255, 0.16)',
                        )(props),
                    },
                    header: {
                        borderTop: mode(
                            '1px solid #A4A4A4',
                            '1px solid rgba(255, 255, 255, 0.16)',
                        )(props),
                        borderBottom: mode(
                            '1px solid #A4A4A4',
                            '1px solid rgba(255, 255, 255, 0.16)',
                        )(props),
                    },
                },
                'header.filter-drawer-header': {
                    '.filter-results': {
                        '.clear-filters': {
                            button: {
                                bg: mode('#F2F2F2', '#595959')(props),
                                border: mode('1px #C3C3C3 solid', '0')(props),
                            },
                        },
                    },
                },
                '.chakra-modal__body.css-79z5gx': {
                    '.filter-section': {
                        borderTop: mode(
                            '1px solid #000',
                            '1px solid rgba(217, 217, 217, 0.4)',
                        )(props),
                        '.radio-filter-container': {
                            '.radio-filter': {
                                label: {
                                    '.filter-tag-selected': {
                                        border: 'none',
                                        bg: mode('#000', '#FFF')(props),
                                        color: mode('#FFF', '#000')(props),
                                    },
                                    '.filter-tag': {
                                        border: mode('1px solid #C3C3C3', 'none')(props),
                                        bg: mode('#F2F2F2', '#595959')(props),
                                        color: mode('#000', '#FFF')(props),
                                    },
                                },
                            },
                        },
                    },
                    '.apply-filters': {
                        borderTop: mode(
                            '1px solid #000',
                            '1px solid rgba(217, 217, 217, 0.4)',
                        )(props),
                        '.apply-button': {
                            bg: mode('#F2F2F2', '#595959')(props),
                            border: mode('1px solid #C3C3C3', 'none')(props),
                        },
                    },
                },
            },
            '.stripe-confirmation-modal': {
                header: {
                    '.currency': {
                        div: {
                            ul: {
                                li: {
                                    borderBottom: mode(
                                        '1px solid #A4A4A4',
                                        '1px solid rgba(255, 255, 255, 0.16)',
                                    )(props),
                                },
                            },
                        },
                    },
                },
            },
            '.game-content': {
                bg: mode('#FFF', '#2e2e2e')(props),
            },
            '.game-content-browser': {
                bg: mode('#FFF', '#2e2e2e')(props),
            },
            '.mobile-content': {
                bg: mode('#FFF', '#2e2e2e')(props),
            },
            '.leaderboard-row': {
                color: mode('#000', '#FFF')(props),
            },
            '.leaderboard-table': {
                '.leaderboard-heading': {
                    color: mode('#000', '#FFF')(props),
                },
                '.leaderboard-stripe': {
                    color: mode('#000', '#FFF')(props),
                    bg: mode('#D9D9D9', '#595959')(props),
                },
            },
            '.details-button': {
                color: mode('#FFF', '#FFF')(props),
            },
            '.games-list': {
                '.games-list-heading': {
                    bg: mode('#FFF', '#2e2e2e')(props),
                },
                '.game-info': {
                    bg: mode('#FFF', '#2e2e2e')(props),
                    border: mode('1px solid #C3C3C3', '1px solid #FECA3C')(props),
                },
            },
            '.leaderboard-tab[aria-selected=true]': {
                color: mode('#000', '#6ef1d5')(props),
            },
            '.past-six-compact-vstack': {
                '.past-six-compact-light': {
                    bg: mode('#F1F1F1', '#222222')(props),
                },
            },
            '.transaction-compact-vstack': {
                '.transaction-compact-light': {
                    bg: mode('#F1F1F1', '#222222')(props),
                },
            },
            '.available-balance': {
                borderTop: mode('1px solid #A4A4A4', '1px solid rgba(255, 255, 255, 0.16)')(props),
                p: {
                    '.currency': {
                        div: {
                            ul: {
                                li: {
                                    '.full-wallet': {
                                        borderTop: mode(
                                            '1px solid #A4A4A4',
                                            '1px solid rgba(255, 255, 255, 0.16)',
                                        )(props),
                                    },
                                },
                            },
                        },
                    },
                },
            },
            div: {
                '.purchase-modal-header': {
                    borderTop: mode(
                        '1px solid #A4A4A4',
                        '1px solid rgba(255, 255, 255, 0.16)',
                    )(props),
                },
                '.purchase-modal-form': {
                    borderTop: mode(
                        '1px solid #A4A4A4',
                        '1px solid rgba(255, 255, 255, 0.16)',
                    )(props),
                },
                '.purchase-modal-footer.css-k0waxj': {
                    borderTop: mode(
                        '1px solid #A4A4A4',
                        '1px solid rgba(255, 255, 255, 0.16)',
                    )(props),
                },
                '.result-modal-footer': {
                    borderTop: mode(
                        '1px solid #A4A4A4',
                        '1px solid rgba(255, 255, 255, 0.16)',
                    )(props),
                },
                '.chakra-modal__header.css-9fgtzh': {
                    '.result-modal-header': {
                        borderTop: mode(
                            '1px solid #A4A4A4',
                            '1px solid rgba(255, 255, 255, 0.16)',
                        )(props),
                    },
                },
            },
            '.compact-live-social-links': {
                svg: {
                    fill: mode(Black, ONLINE_COLOR)(props),
                },
            },
            '.compact-live-social-links.following': {
                svg: {
                    fill: mode(Black, FOLLOWING_COLOR)(props),
                },
            },
            '.follower-list-item': {
                bg: mode('#FFF', '#2E2E2E')(props),
                border: mode('1px solid #C3C3C3', 'none')(props),
            },
            '.following-search': {
                bg: mode('#FFF', '#000')(props),
            },
            '.settings-container': {
                '.settings-switch': {
                    '.chakra-switch__track': {
                        bg: mode('#2E2E2E', '#2E2E2E')(props),
                        _checked: {
                            bg: mode('#2E2E2E', '#2E2E2E')(props),
                        },
                    },
                    '.chakra-switch__thumb': {
                        bg: mode('#6867DD', '#6867DD')(props),
                    },
                },
                '.settings-button': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _disabled: {
                        _hover: {
                            bg: mode('#595959', '#595959')(props),
                        },
                    },
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
                '.settings-button-pink': {
                    bg: mode('#FE8FB6', '#FE8FB6')(props),
                    color: mode('#000000', '#000000')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#FE8FB6', '#FE8FB6')(props),
                    },
                },
                '.settings-icon': {
                    color: mode('#FFFFFF', '#A4A4A4')(props),
                    bg: mode('#6867DD', '#6867DD')(props),
                },
                '.settings-checkbox': {
                    color: mode('#6867DD', '#6867DD')(props),
                    '.chakra-checkbox__control': {
                        bg: mode('#A4A4A4', '#A4A4A4')(props),
                        borderColor: mode('#6867DD', '#6867DD')(props),
                        _checked: {
                            borderColor: mode('#6867DD', '#6867DD')(props),
                            bg: mode('#6867DD', '#6867DD')(props),
                            color: mode('#FFFFFF', '#FFFFFF')(props),
                        },
                    },
                },
            },
            '.membership-items': {
                bg: mode('#FFFFFF', '#000000')(props),
                borderColor: mode('#6867DD', '#6867DD')(props),
            },
            '.membership-divider': {
                bg: mode('#A4A4A4', '#D9D9D9')(props),
            },
            '.edit-profile': {
                '.divider': {
                    borderColor: mode('#A4A4A4', '#d9d9d9')(props),
                    opacity: mode('100%', '40%')(props),
                },
                input: {
                    borderColor: mode('#A4A4A4', '#D9D9D966')(props),
                },
                textarea: {
                    borderColor: mode('#A4A4A4', '#D9D9D966')(props),
                },
                '.edit-profile-avatar-image': {
                    border: mode('1px solid #C3C3C3', '1px solid #FFF')(props),
                    backgroundColor: mode('#FFF', '#000')(props),
                },
                '.upload-image-controls-clear': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
                '.upload-image-controls-update': {
                    bg: mode('#FE8FB6', '#FE8FB6')(props),
                    color: mode('#000000', '#000000')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#FE8FB6', '#FE8FB6')(props),
                    },
                },
            },
            '.edit-profile-browser': {
                '.divider': {
                    borderColor: mode('#A4A4A4', '#d9d9d9')(props),
                    opacity: mode('100%', '40%')(props),
                },
                input: {
                    borderColor: mode('#A4A4A4', '#D9D9D966')(props),
                },
                textarea: {
                    borderColor: mode('#A4A4A4', '#D9D9D966')(props),
                },
                '.edit-profile-avatar-image': {
                    border: mode('1px solid #C3C3C3', '1px solid #FFF')(props),
                    backgroundColor: mode('#FFF', '#000')(props),
                },
                '.upload-image-controls-clear': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
                '.upload-image-controls-update': {
                    bg: mode('#FE8FB6', '#FE8FB6')(props),
                    color: mode('#000000', '#000000')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#FE8FB6', '#FE8FB6')(props),
                    },
                },
            },
            '.setup-profile-container': {
                '.setup-profile-divider': {
                    borderColor: mode('#A4A4A4', '#d9d9d9')(props),
                    opacity: mode('100%', '40%')(props),
                },
                '.upload-image-controls-clear': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
                '.upload-image-controls-update': {
                    bg: mode('#FE8FB6', '#FE8FB6')(props),
                    color: mode('#000000', '#000000')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#FE8FB6', '#FE8FB6')(props),
                    },
                },
                '.profile-info-input': {
                    bg: mode('#FFFFFF', '#FFFFFF')(props),
                    color: mode('#000000', '#000000')(props),
                    _placeholder: {
                        color: mode('#000000', '#000000')(props),
                    },
                },
                '.profile-info-divider': {
                    borderColor: mode('#A4A4A4', '#d9d9d9')(props),
                    opacity: mode('100%', '40%')(props),
                },
                '.setup-profile-button': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
            },
            '.setup-membership-container': {
                '.setup-membership-button': {
                    bg: mode('#595959', '#595959')(props),
                    color: mode('#FFFFFF', '#FFFFFF')(props),
                    _disabled: {
                        _hover: {
                            bg: mode('#595959', '#595959')(props),
                        },
                    },
                    _hover: {
                        textDecoration: 'underline',
                        bg: mode('#595959', '#595959')(props),
                    },
                },
            },
            '.sixes-browser': {
                backgroundColor: mode('#FFF', '#2e2e2e')(props),
                border: mode('1px solid #C3C3C3', 'none')(props),
            },
            '.profile-tab-title[aria-selected=true]': {
                color: mode('#000', '#FFF')(props),
            },
            '.wallet-row-stripe': {
                bg: mode('#ECECEC', '#2E2E2E')(props),
            },
            '.header-menu-body': {
                svg: {
                    color: mode('#000', '#FFF')(props),
                },
            },
            '.my-balance-container': {
                bg: mode('#FFF', '#161616')(props),
            },
            '.ingame-items-container': {
                '.ingame-balance': {
                    bg: mode('#FFF', '#161616')(props),
                },
            },
            '.ingame-product-section': {
                '.ingame-balance': {
                    bg: mode('#FFF', '#161616')(props),
                },
            },
            '.background-row-light': {
                bg: mode('#FFF', '#2e2e2e')(props),
            },
            '.background-row-dark': {
                bg: mode('#EBEBEB', '#222222')(props),
            },
            '.wrapper-checkbox-background': {
                bg: mode('#C3C3C3', '#2e2e2e')(props),
            },
            '.sort-options': {
                '.sort-option-button': {
                    backgroundColor: mode('#F2F2F2', '#595959')(props),
                    border: mode('1px #c3c3c3 solid', '')(props),
                },
                ".sort-option-button[data-active='']": {
                    border: 'none',
                    backgroundColor: mode('black', 'white')(props),
                    color: mode('white', 'black')(props),
                },
            },
            'section.dashboard-modal-content, section.setup-2fa-content, .installation-modal-content':
                {
                    maxWidth: '300px',
                    borderRadius: 0,
                    'header, div, footer': {
                        bg: mode('#DEDEDE', '#2E2E2E')(props),
                    },
                },
            '.dashboard-browser-view': {
                '.dashboard-browser-bottom-section': {
                    '.dashboard-browser-columns.browser-content': {
                        '.browser-section:nth-child(even)': {
                            background: mode('#C3C3C3', 'none')(props),
                        },
                    },
                },
            },
        }),
    },
});

export default theme;
